import { useState, useEffect } from 'react'

export function AnimatedExplainer() {
  const [currentStep, setCurrentStep] = useState(0)
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % 3)
    }, 4000) // Change step every 4 seconds
    
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="border-cycle relative w-full h-full rounded-2xl overflow-hidden" style={{
      background: 'rgba(25,25,25,0.4)',
      backdropFilter: 'blur(15px)',
      WebkitBackdropFilter: 'blur(15px)',
      border: '1px solid rgba(209,209,247,0.5)'
    }}>
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-10 left-10 w-32 h-32 bg-[#D4AF37] rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-[#235C45] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Main content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center p-12">
        
        {/* Step indicator */}
        <div className="flex gap-2 mb-8">
          {[0, 1, 2].map((step) => (
            <div
              key={step}
              className={`h-2 rounded-full transition-all duration-500 ${
                currentStep === step ? 'w-12' : 'w-2'
              }`}
              style={{ background: currentStep === step ? '#8E8FFA' : 'rgba(255,255,255,0.3)' }}
            />
          ))}
        </div>

        {/* Animation container */}
        <div className="relative w-full max-w-md h-80">
          
          {/* Step 1: Upload */}
          <div
            className={`absolute inset-0 transition-all duration-700 ${
              currentStep === 0 ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
          >
            <div className="flex flex-col items-center justify-center h-full">
              {/* Phone mockup */}
              <div className="relative w-64 h-64 rounded-3xl shadow-2xl p-6 animate-float" style={{
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(142,143,250,0.4)',
                boxShadow: '0 0 0 1px rgba(255,182,193,0.2), 0 20px 60px rgba(0,0,0,0.3)'
              }}>
                {/* Upload icon animation */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg className="w-24 h-24 animate-bounce-slow" fill="none" stroke="url(#uploadGrad)" viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 0 12px rgba(142,143,250,0.8)) drop-shadow(0 0 24px rgba(142,143,250,0.4))' }}>
                    <defs>
                      <linearGradient id="uploadGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#D1D1F7" />
                        <stop offset="100%" stopColor="#8E8FFA" />
                      </linearGradient>
                    </defs>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                {/* Sparkles */}
                <div className="absolute top-4 right-4 text-[#D4AF37] animate-ping">✨</div>
                <div className="absolute bottom-4 left-4 text-[#D4AF37] animate-ping" style={{ animationDelay: '0.5s' }}>✨</div>
              </div>
              <p className="mt-6 text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#ffffff', textShadow: '0 0 12px rgba(212,175,55,0.4)' }}>
                Upload Your Image
              </p>
              <p className="text-sm text-muted mt-2">AI analyzes instantly</p>
            </div>
          </div>

          {/* Step 2: Choose Mood */}
          <div
            className={`absolute inset-0 transition-all duration-700 ${
              currentStep === 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
          >
            <div className="flex flex-col items-center justify-center h-full">
              {/* Mood selector animation */}
              <div className="relative w-64 h-64 rounded-3xl shadow-2xl p-6" style={{
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(142,143,250,0.4)',
                boxShadow: '0 0 0 1px rgba(255,182,193,0.2), 0 20px 60px rgba(0,0,0,0.3)'
              }}>
                <div className="grid grid-cols-2 gap-3 h-full">
                  {['😌', '💪', '😎', '❤️'].map((emoji, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-br from-[#235C45]/10 to-[#D4AF37]/10 rounded-xl flex items-center justify-center text-4xl animate-scale-in hover:scale-110 transition-transform cursor-pointer"
                      style={{ animationDelay: `${index * 0.2}s` }}
                    >
                      {emoji}
                    </div>
                  ))}
                </div>
                {/* Selection indicator */}
                <div className="absolute -top-2 -right-2 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold animate-bounce-slow" style={{
                  background: 'linear-gradient(135deg, #8E8FFA 0%, #D1D1F7 40%, #FFB6C1 100%)',
                  boxShadow: '0 0 16px rgba(142,143,250,0.7), 0 0 32px rgba(255,182,193,0.4), 0 4px 12px rgba(0,0,0,0.3)'
                }}>
                  ✓
                </div>
              </div>
              <p className="mt-6 text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#ffffff', textShadow: '0 0 12px rgba(212,175,55,0.4)' }}>
                Set the Mood
              </p>
              <p className="text-sm text-muted mt-2">Pick your vibe</p>
            </div>
          </div>

          {/* Step 3: Get Captions */}
          <div
            className={`absolute inset-0 transition-all duration-700 ${
              currentStep === 2 ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
          >
            <div className="flex flex-col items-center justify-center h-full">
              {/* Captions appearing animation */}
              <div className="relative w-64 h-64 rounded-3xl shadow-2xl p-6 overflow-hidden" style={{
                background: 'rgba(15,15,15,0.4)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(142,143,250,0.4)',
                boxShadow: '0 0 0 1px rgba(255,182,193,0.15), 0 20px 60px rgba(0,0,0,0.5)'
              }}>
                <div className="space-y-3">
                  {[0, 1, 2, 3].map((index) => (
                    <div
                      key={index}
                      className="rounded-lg p-3 animate-slide-in-right"
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        borderTop: '1px solid rgba(255,255,255,0.1)',
                        animationDelay: `${index * 0.3}s`
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{
                          background: '#D4AF37',
                          boxShadow: '0 0 8px rgba(212,175,55,0.7), 0 0 16px rgba(212,175,55,0.3)'
                        }}>
                          {index + 1}
                        </div>
                        <div className="flex-1 h-2 rounded" style={{ background: 'rgba(255,255,255,0.85)' }}></div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Floating emojis */}
                <div className="absolute top-2 right-2 text-2xl animate-float" style={{ filter: 'drop-shadow(0 0 8px rgba(142,143,250,0.9)) drop-shadow(0 0 16px rgba(142,143,250,0.5))' }}>✨</div>
                <div className="absolute bottom-2 right-2 text-2xl animate-float" style={{ animationDelay: '0.5s', filter: 'drop-shadow(0 0 8px rgba(224,64,251,0.9)) drop-shadow(0 0 16px rgba(224,64,251,0.5))' }}>💫</div>
              </div>
              <p className="mt-6 text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#ffffff', textShadow: '0 0 12px rgba(212,175,55,0.4)' }}>
                Get Perfect Captions
              </p>
              <p className="text-sm text-muted mt-2">With emojis & hashtags</p>
            </div>
          </div>

        </div>

        {/* Bottom text */}
        <div className="mt-8 text-center">
          <p className="text-sm text-muted">
            {currentStep === 0 && "Drop your photo and let AI do the work"}
            {currentStep === 1 && "Choose from 8 different moods"}
            {currentStep === 2 && "Copy and post in seconds"}
          </p>
        </div>
      </div>
    </div>
  )
}
